import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import { updateSection } from "../../../assignmentSlice";
import { useTTS } from "../../../../../hooks/useTTS";
import { getContentHeight } from "../../../../../utils/textUtils";
import StepsBar from "../../widgets/StepsBar";

// ECHO 시안 02 — 지문 읽기
// PC: 좌(영어 본문 전체 리스트 + 문장 듣기) / 우(우리말 해석 + 보기·가리기 토글)
// 모바일: 문장 카드(영+한 함께), 현재 카드 teal 테두리 + "이 문장 듣기"
// hide_translation 과제는 출제 의도대로 해석을 아예 제공하지 않는다(토글 없음).
const FONT =
  "'Pretendard','Spoqa Han Sans Neo','Apple SD Gothic Neo','Noto Sans KR',sans-serif";

const CIRCLED = "①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳";
const numOf = (i) => (i < 20 ? CIRCLED[i] : `${i + 1}.`);

const paneStyle = {
  backgroundColor: "#fff",
  border: "1px solid #E6EDEC", // --line
  borderRadius: "14px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
};
const phStyle = {
  padding: "9px 14px",
  borderBottom: "1px solid #E6EDEC",
  fontSize: "13px",
  fontWeight: 800,
  letterSpacing: "0.06em",
  color: "#00A396", // --teal-deep
  display: "flex",
  alignItems: "center",
  gap: "6px",
};
const listenStyle = {
  marginLeft: "auto",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  backgroundColor: "#0E141B", // --ink
  color: "#fff",
  fontSize: "12px",
  fontWeight: 700,
  border: "none",
  borderRadius: "999px",
  padding: "6px 12px",
  cursor: "pointer",
};
const tabStyle = (on) => ({
  fontSize: "11px",
  fontWeight: 700,
  padding: "3px 10px",
  borderRadius: "999px",
  border: `1px solid ${on ? "#E5FBF8" : "#E6EDEC"}`,
  backgroundColor: on ? "#E5FBF8" : "#fff", // --teal-soft
  color: on ? "#00A396" : "#7A8490",
  cursor: "pointer",
});
const submitStyle = {
  backgroundColor: "#0E141B",
  color: "#fff",
  border: "none",
  borderRadius: "999px",
  padding: "8px 24px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
};
const pfootStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "10px",
  paddingTop: "10px",
  marginTop: "auto",
};
const pcountStyle = {
  fontSize: "13px",
  color: "#7A8490", // --ink-45
  fontWeight: 700,
  marginRight: "auto",
};

const ReadingPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showKor, setShowKor] = useState(true);
  const [isMobile, setIsMobile] = useState(Math.min(window.innerWidth, window.screen.width) <= 768);
  const currentRef = useRef(null);

  const hideTranslation = section.question?.hide_translation ?? false;

  useEffect(() => {
    if (!section) return;
    setPageList(section.question.data);
  }, [section]);

  useEffect(() => {
    const onResize = () => setIsMobile(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [currentIndex]);

  const { speak, stopSpeaking } = useTTS();

  const handleSelect = (idx) => {
    stopSpeaking();
    setCurrentIndex(idx);
    speak({ text: pageList[idx][0] });
  };

  const handlePlay = () => {
    stopSpeaking();
    if (pageList[currentIndex]) speak({ text: pageList[currentIndex][0] });
  };

  const handleNext = () => {
    stopSpeaking();
    if (currentIndex < pageList.length - 1) {
      const next = currentIndex + 1;
      speak({ text: pageList[next][0] });
      setCurrentIndex(next);
    } else {
      if (!isPreview && !isReview) {
        dispatch(
          updateSection({ sectionId: section.id, data: { is_finished: true } })
        );
      }
      alert("학습이 완료되었습니다.");
      setCurrentStep(2);
    }
  };

  const footer = (
    <div style={pfootStyle}>
      <span style={pcountStyle}>
        {pageList.length > 0 ? currentIndex + 1 : 0} / {pageList.length}
        {isMobile ? "" : " 문장"}
      </span>
      <button style={submitStyle} onClick={handleNext}>
        다음 →
      </button>
    </div>
  );

  // ── 모바일: 문장 카드(영+한 함께) ──
  if (isMobile) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: getContentHeight(true),
          fontFamily: FONT,
          backgroundColor: "#FFFFFF",
        }}
      >
        <StepsBar />
        <div style={{ flex: 1, minHeight: 0, padding: "12px", display: "flex" }}>
          <div style={{ ...paneStyle, flex: 1 }}>
            <div style={phStyle}>
              지문 읽기
              {!hideTranslation && (
                <span style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
                  <button style={tabStyle(showKor)} onClick={() => setShowKor(true)}>
                    해석 보기
                  </button>
                  <button style={tabStyle(!showKor)} onClick={() => setShowKor(false)}>
                    가리기
                  </button>
                </span>
              )}
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "12px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {pageList.map((sentence, idx) => {
                const isOn = idx === currentIndex;
                return (
                  <div
                    key={idx}
                    ref={isOn ? currentRef : null}
                    onClick={() => handleSelect(idx)}
                    style={{
                      // 시안 02 모바일 카드: 현재 = teal 테두리 + teal-mist 배경
                      border: isOn ? "1.5px solid #00D9C0" : "1px solid #E6EDEC",
                      backgroundColor: isOn ? "#F2FAF8" : "#fff",
                      borderRadius: "10px",
                      padding: "10px 12px",
                      marginBottom: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: "15px", lineHeight: 1.7, color: "#0E141B" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 800,
                          color: "#00A396",
                          verticalAlign: "super",
                          marginRight: "3px",
                        }}
                      >
                        {numOf(idx)}
                      </span>
                      {sentence[0]}
                    </div>
                    {!hideTranslation && showKor && sentence[1] && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#3A434E",
                          lineHeight: 1.75,
                          paddingTop: "5px",
                        }}
                      >
                        {sentence[1]}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                style={{ ...listenStyle, margin: "4px auto 0", display: "flex" }}
                onClick={handlePlay}
              >
                ▶ 이 문장 듣기
              </button>
              {footer}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── PC: 좌우 두 패널 ──
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: getContentHeight(false),
        fontFamily: FONT,
        backgroundColor: "#FFFFFF",
      }}
    >
      <StepsBar />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: "16px 20px",
          display: "flex",
          gap: "12px",
          maxWidth: "1160px",
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* 영어 본문 패널 */}
        <div style={{ ...paneStyle, flex: 1.1 }}>
          <div style={phStyle}>
            영어 본문
            <button style={listenStyle} onClick={handlePlay}>
              ▶ 문장 듣기
            </button>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "12px 15px" }}>
            {pageList.map((sentence, idx) => {
              const isOn = idx === currentIndex;
              return (
                <div
                  key={idx}
                  ref={isOn ? currentRef : null}
                  onClick={() => handleSelect(idx)}
                  style={{
                    // 시안 02 read-sent(.on = --teal-soft)
                    padding: "8px 10px",
                    borderRadius: "8px",
                    fontSize: "16px",
                    lineHeight: 1.7,
                    cursor: "pointer",
                    color: "#0E141B",
                    backgroundColor: isOn ? "#E5FBF8" : "transparent",
                  }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 800,
                      color: "#00A396",
                      verticalAlign: "super",
                      marginRight: "3px",
                    }}
                  >
                    {numOf(idx)}
                  </span>
                  {sentence[0]}
                </div>
              );
            })}
          </div>
        </div>

        {/* 우리말 해석 패널 — hide_translation 과제에서는 렌더링하지 않음 */}
        {!hideTranslation ? (
          <div style={{ ...paneStyle, flex: 1 }}>
            <div style={phStyle}>
              우리말 해석
              <span style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
                <button style={tabStyle(showKor)} onClick={() => setShowKor(true)}>
                  보기
                </button>
                <button style={tabStyle(!showKor)} onClick={() => setShowKor(false)}>
                  가리기
                </button>
              </span>
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "12px 15px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {showKor ? (
                pageList.map((sentence, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSelect(idx)}
                    style={{
                      // 시안 02 kor-line(.on = --sage)
                      fontSize: "14px",
                      color: "#3A434E",
                      lineHeight: 1.75,
                      padding: "8px 10px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor:
                        idx === currentIndex ? "#F4F8F7" : "transparent",
                    }}
                  >
                    {sentence[1]}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#B9C1C9",
                    fontSize: "14px",
                  }}
                >
                  해석을 가렸습니다
                </div>
              )}
              {footer}
            </div>
          </div>
        ) : (
          <div
            style={{
              ...paneStyle,
              flex: 1,
              border: "none",
              backgroundColor: "transparent",
              justifyContent: "flex-end",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingPage;
